'use client'
import { Grid } from '@achieve/ascend'
import { MediaImageStandard, Typography } from 'components/Contentful'
import { _get } from 'utils/shared'
import { Eyebrow } from 'components/Eyebrow'
import { AchieveLink } from 'components/AchieveLink'
import { getSlug, normalizeCategoryName } from 'utils/conversions'
import { contentMap } from 'utils/contentful'
import { useAppRouter } from 'hooks/useAppRouter/useAppRouter'
import { AUTHORS_ROUTE, LEARN_ROUTE } from 'constants/navigation'
import { useEffect, useReducer, useState } from 'react'
import { Carousel } from 'components/Carousels'
import { normalizeContent } from 'utils/shared/hacks'
import { AuthorAvatar } from 'components/AuthorAvatar'
import { Image } from 'components/Image'
import useOnScreen from 'hooks/useOnScreen'
import { mainProductConfig } from 'constants/mainProductConfig'
import classNames from 'classnames'

const FEATURED_LOOP_INTERVAL = 5000

function ArticleCard({ item, index, sectionVariationObject }) {
  const { title, hook, slug, primaryCategory, mainImage, author } = item
  const articleLink = `${LEARN_ROUTE}/${getSlug(primaryCategory)}/${slug}`
  const categoryLink = `${LEARN_ROUTE}/${getSlug(primaryCategory)}`
  const siteEnv = process.env.NEXT_PUBLIC_SITE_ENVIRONMENT || 'achieve'

  const ArticleLink = ({ children }) => {
    return (
      <AchieveLink
        href={articleLink}
        track={{
          list_name: `ARTICLE LINK`,
          position: index,
          click_id: `${mainProductConfig[siteEnv || 'achieve']?.['MainProduct']} | ${title}`,
          click_text: title,
          click_url: articleLink,
          nav_link_section: 'Article',
          track_event: 'link_click',
          click_type: 'Link Click',
          event_action: 'link_click',
          event_type: 'link_click',
          sectionVariationObject,
        }}
        className={classNames(
          'text-black no-underline cursor-pointer',
          'hover:underline',
          'data-[active=true]:underline',
          'hover:data-[testid="card-title"]:underline hover:data-[testid="card-title"]:text-blue-400 hover:data-[testid="card-title"]:font-bold',
          '[&_h3]:hover:underline [&_h3]:hover:text-black'
        )}
      >
        {children}
      </AchieveLink>
    )
  }
  return (
    <div className={'flex flex-col gap-3 w-screen max-w-[100vw]'}>
      <ArticleLink sectionVariationObject={sectionVariationObject}>
        <div
          className={
            'relative max-h-full max-w-full w-screen [&_div]:h-full [&_img]:absolute [&_img]:box-border [&_img]:block [&_img]:w-0 [&_img]:h-0 [&_img]:min-w-full [&_img]:max-w-full [&_img]:min-h-full [&_img]:max-h-full [&_img]:object-cover [&_img]:object-[center_center] [&_img]:m-auto [&_img]:p-0[&_img]:rounded-none [&_img]:border-[none] [&_img]:inset-0'
          }
        >
          <div>
            <MediaImageStandard
              className={'w-full'}
              alt={mainImage?.mediaAltText}
              content={mainImage?.mediaContent}
              aspectRatio={2 / 1}
              fetchPriority="high"
              preload={true}
            />
          </div>
        </div>
      </ArticleLink>
      <div className={'flex flex-col gap-3 px-4 py-0 md:px-8 md:py-0'}>
        <AchieveLink
          className={'text-blue-300'}
          href={categoryLink}
          data-testid="article-hero-eyebrow-link"
        >
          <Eyebrow
            className={classNames('text-start', 'text-left text-blue-500 pb-0')}
            content={primaryCategory}
            component="span"
          />
        </AchieveLink>
        <ArticleLink>
          <Typography variant="headingMd" content={title} component="p" />
        </ArticleLink>
        <Typography variant="bodySm" content={hook} />
        <AuthorAvatar
          authorPhoto={author?.authorPhoto}
          index={0}
          href={`${AUTHORS_ROUTE}/` + (author?.slug || getSlug(author?.authorName))}
          authorName={author?.authorName}
        />
      </div>
    </div>
  )
}

const RelatedArticles = ({ content }) => {
  const sectionVariationObject = {
    ...content?.fields?.experiment,
    variant: content?.fields?.experimentVariation,
  }
  const { query } = useAppRouter()
  const [onPause, setOnPause] = useState(false)
  const { ref, isIntersecting } = useOnScreen({ once: false })

  const reducerFunction = (state, action) => {
    switch (action.type) {
      case 'setData':
        return { ...state, ...action.payload }
      default:
        return state
    }
  }

  const readContentData = (content) => {
    const mainListArticlesCategory = _get(content, 'fields.mainListArticlesCategory')
    if (mainListArticlesCategory) {
      const articles = _get(mainListArticlesCategory, 'relatedContentCollection.items') || []

      const { listTitle } = contentMap(
        normalizeContent(mainListArticlesCategory?.sectionContentsCollection?.items) || [],
        ['listTitle']
      )
      return {
        relatedArticles: articles,
        listTitle,
        mainItem: _get(articles, '[0]'),
      }
    } else {
      const { listTitle } = contentMap(normalizeContent(_get(content, 'fields.sectionContents')), [
        'listTitle',
      ])
      return {
        relatedArticles: _get(content, 'fields.relatedContentCollection.items'),
        listTitle,
        mainItem: _get(content, 'fields.relatedContentCollection.items[0]'),
      }
    }
  }
  const data = readContentData(content)

  const [{ listTitle, mainItem, relatedArticles }, dispatchLocal] = useReducer(reducerFunction, {
    listTitle: data.listTitle,
    mainItem: data.mainItem,
    primaryCategory: normalizeCategoryName(query?.categoryname ?? ''),
    relatedArticles: data.relatedArticles,
  })

  useEffect(() => {
    dispatchLocal({
      type: 'setData',
      payload: {
        primaryCategory: normalizeCategoryName(query?.categoryname ?? ''),
      },
    })
  }, [query.categoryname])

  const setMainItem = (item) => [
    dispatchLocal({
      type: 'setData',
      payload: {
        mainItem: item,
      },
    }),
  ]

  const renderReleatedArticles = () => {
    return relatedArticles.map((fields, index) => {
      const isVideo = fields.mainImage.__typename === 'VideoPlayer'
      const alt = isVideo ? fields?.mainImage?.altText : fields?.mainImage?.mediaAltText
      const img = isVideo ? fields?.mainImage?.thumbnail : fields?.mainImage?.mediaContent

      return (
        <div
          className={classNames(
            'flex flex-row gap-2 border-t-neutral-150 px-0 py-4 border-t border-solid border-b-0 border-x-0 text-black no-underline cursor-pointer hover:underline data-[active=true]:underline',
            'hover:data-[testid="card-title"]:underline hover:data-[testid="card-title"]:text-blue-400 hover:data-[testid="card-title"]:font-bold',
            'data-[active=true]:data-[testid="card-title"]:underline data-[active=true]:data-[testid="card-title"]:text-blue-400 data-[active=true]:data-[testid="card-title"]:font-bold'
          )}
          key={`releatedItems-${index}`}
          onClick={() => {
            setMainItem(fields)
            setOnPause(true)
          }}
          data-active={fields.slug === mainItem?.slug}
          data-testid="releated-card-item"
        >
          <div className={'relative h-[70px]'}>
            <MediaImageStandard
              className={'rounded-md'}
              alt={alt}
              content={img}
              layout="fixed"
              objectFit="cover"
              height={70}
              width={70}
              fetchPriority="high"
              preload={true}
              data-testid="releated-card-item-image"
            />
            {isVideo && (
              <div
                className={'absolute -translate-x-2/4 -translate-y-2/4 z-1 flex left-2/4 top-2/4'}
              >
                <Image alt={'Play Button'} height={40} src="/play-btn.svg" width={40} />
              </div>
            )}
          </div>
          <Typography
            className={''}
            variant="bodySm"
            content={fields?.title}
            data-testid="releated-card-item-title"
          />
        </div>
      )
    })
  }

  const onClickNext = () => {
    const currentIndex = relatedArticles.findIndex((item) => item.slug === mainItem.slug)
    const nextIndex = currentIndex + 1
    const nextItem = relatedArticles[nextIndex] || relatedArticles[0]
    setMainItem(nextItem)
  }

  useEffect(() => {
    if (!onPause && isIntersecting) {
      const intervalId = setInterval(() => {
        onClickNext()
      }, FEATURED_LOOP_INTERVAL)
      return () => clearInterval(intervalId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainItem, relatedArticles, onPause, isIntersecting])

  const ArticleLink = ({ children }) => {
    return (
      <AchieveLink
        key={`releatedItems`}
        track={{
          list_name: 'Card Article',
          click_position: 0,
          click_id: mainItem?.title,
          click_text: mainItem?.title,
          nav_link_section: 'Content Grid',
          track_event: 'page_nav_click',
          event_action: 'link_click',
          event_type: 'link_click',
          sectionVariationObject,
        }}
        className={classNames(
          'text-black no-underline cursor-pointer',
          'hover:underline',
          'data-[active=true]:underline',
          'hover:data-[testid="card-title"]:underline hover:data-[testid="card-title"]:text-blue-400 hover:data-[testid="card-title"]:font-bold',
          '[&_h3]:hover:underline [&_h3]:hover:text-black'
        )}
        href={`${LEARN_ROUTE}/${getSlug(mainItem?.primaryCategory)}/${mainItem?.slug}`}
        data-testid="article-hero-title-link"
      >
        {children}
      </AchieveLink>
    )
  }

  return (
    <>
      {relatedArticles?.length > 0 && !query?.search && (
        <Grid
          container
          spacing={2}
          marginTop={1}
          className={'hidden lg:flex'}
          data-testid="article-hero-container-desktop"
          ref={ref}
        >
          <Grid
            size={{ xs: 12, lg: 9 }}
            className={'flex flex-col gap-2'}
            data-testid="article-hero-main-wrapper"
          >
            <ArticleLink>
              <div className={'relative mb-4'} data-testid="article-hero-image-container">
                <MediaImageStandard
                  className={'rounded-md'}
                  alt={
                    mainItem.mainImage.__typename === 'VideoPlayer'
                      ? mainItem?.mainImage?.altText
                      : mainItem?.mainImage?.mediaAltText
                  }
                  content={
                    mainItem.mainImage.__typename === 'VideoPlayer'
                      ? mainItem?.mainImage?.thumbnail
                      : mainItem?.mainImage?.mediaContent
                  }
                  data-testid="article-hero-image"
                  fetchPriority="high"
                  preload={true}
                  aspectRatio={2 / 1}
                />
                {mainItem?.mainImage.__typename === 'VideoPlayer' && (
                  <div
                    className={
                      'absolute -translate-x-2/4 -translate-y-2/4 z-1 flex left-2/4 top-2/4'
                    }
                  >
                    <Image alt={'Play Button'} height={64} src="/play-btn.svg" width={64} />
                  </div>
                )}
              </div>
            </ArticleLink>
            <AchieveLink
              className={'text-blue-300'}
              href={`${LEARN_ROUTE}/${getSlug(mainItem?.primaryCategory)}`}
              data-testid="article-hero-eyebrow-link"
            >
              <Eyebrow
                className={classNames('text-start', 'text-left text-blue-500 pb-0')}
                content={mainItem?.primaryCategory}
                component="span"
                data-testid="article-hero-eyebrow-link-text"
              />
            </AchieveLink>
            <ArticleLink>
              <Typography
                variant="headingLg"
                content={mainItem?.title}
                component="h3"
                data-testid="article-hero-title-link-text"
              />
            </ArticleLink>
            {mainItem?.hook && (
              <Typography
                variant="bodySm"
                content={mainItem?.hook}
                data-testid="article-hero-hook"
              />
            )}
            {mainItem?.author?.authorName && (
              <AuthorAvatar
                authorPhoto={mainItem?.author?.authorPhoto}
                index={0}
                href={
                  `${AUTHORS_ROUTE}/` +
                  (mainItem?.author?.slug || getSlug(mainItem?.author?.authorName))
                }
                authorName={mainItem?.author?.authorName}
                fetchPriority="high"
                preload={true}
              />
            )}
          </Grid>
          <Grid size={{ xs: 12, lg: 3 }} data-testid="article-hero-related-card-container">
            <div
              className={
                'releated-card-item-container flex-col gap-2 w-full rounded hidden p-6 lg:flex bg-blue-25'
              }
            >
              <Typography
                variant="displaySm"
                content={listTitle?.textContent}
                data-testid="related-card-title-text"
                component="h2"
              />
              <div
                className={'last:pb-0 [&_.releated-card-item-container]:first:border-t-0'}
                data-testid="article-hero-related-card-list-container"
              >
                {renderReleatedArticles()}
              </div>
            </div>
          </Grid>
        </Grid>
      )}
      {relatedArticles?.length > 0 && !query?.search && (
        <Grid
          container
          spacing={2}
          marginTop={2}
          className={'flex w-screen md:-ml-8 lg:hidden'}
          data-testid="article-hero-container-mobile"
        >
          <Carousel
            carouselItems={relatedArticles}
            className={'p-0'}
            classNameContainer={''}
            classNameItems={'pl-0'}
            itemComponent={ArticleCard}
            variant="auto"
            idPrefix={'hero-article-list'}
            withIndicators={true}
            classNameControls={''}
            classNameIndicatorDots={'bg-neutral-300 my-6'}
            autoPlay={true}
            infinityLoop={true}
            interval={FEATURED_LOOP_INTERVAL}
          />
        </Grid>
      )}
    </>
  )
}

export { RelatedArticles }
export default RelatedArticles
